<template>
  <div class="sm:px-1" >
    <v-chart  class="chart lg:h-96 md:h-64 sm:h-40 h-32 w-full" :option="chartOption" autoresize />
    <div class="flex justify-between items-center mb-3 mr-3 ml-3">
      <span class="text-m font-normal text-gray-400">{{ $gettext("Risque faible") }}</span>
      <span class="bg-green-50 text-green-400 px-3 py-1 rounded-full text-sm font-bold">{{ countRiskLevelOccurences("low") }}</span>
    </div>
    <div class="flex justify-between items-center mb-3 mr-3 ml-3">
      <span class="text-m font-normal text-gray-400">{{ $gettext("Risque standard") }}</span>
      <span class="bg-amber-50 text-amber-400 px-3 py-1 rounded-full text-sm font-bold">{{ countRiskLevelOccurences("medium") }}</span>
    </div>
    <div class="flex justify-between items-center mb-3 mr-3 ml-3">
      <span class="text-m font-normal text-gray-400">{{ $gettext("Risque élevé") }}</span>
      <span class="bg-red-50 text-red-400 px-3 py-1 rounded-full text-sm font-bold">{{ countRiskLevelOccurences("high") }}</span>
    </div>
  </div>
</template>

<script setup>
import VChart from "vue-echarts";
import { use } from "echarts/core";
import { CanvasRenderer } from "echarts/renderers";
import { PieChart } from "echarts/charts";
import { GraphicComponent } from "echarts/components";
import { ref, watchEffect, computed } from "vue";

const props = defineProps({
  riskScoring: Object
});

use([
  CanvasRenderer,
  PieChart,
  GraphicComponent
]);

const riskScoring = computed(() => props.riskScoring);

const mainColors = ["#05df72", "#ffba00", "#ff6467"];

const chartOption = ref({
  color: mainColors,
    graphic: [
    {
      type: "group",
      left: "center",
      top: "center",
      children: [
        {
          type: "rect",
          left: "center",
          top: "middle",
          shape: {
            width: 60,
            height: 40,
          },
          style: {
            fill: "",
            borderRadius: 90,
          },
        },
        {
          type: "text",
          left: "center",
          top: "middle",
          style: {
            text: "",
            fill: "",
            fontSize: 24,
            fontWeight: "bold",
            textAlign: "center",
            textVerticalAlign: "middle",
          },
        },
      ],
    },
  ],
  series: [
    {
      type: "pie",
      radius: ["55%", "75%"],
      center: ["50%", "50%"],
      top: "0",
      bottom: "0",
      left: "0",
      right: "0",
      padAngle: 1,
      labelLine: {
        show: false,
      },
      label: {
        show: false,
        position: "center",
      },
      data: [],
    },
  ],
});

const countRiskLevelOccurences = (level) => {
  const riskScoringRuleExecutions =
    riskScoring.value?.relationships?.riskScoringRuleExecutions?.data;
  if (!riskScoringRuleExecutions) return 0;

  return riskScoringRuleExecutions.filter(
    (execution) =>
      execution?.attributes?.level === level &&
      !execution?.attributes?.skipped
  ).length;
};

const computeCurrentScore = () => {
  const riskScoringRuleExecutions =
    riskScoring.value?.relationships?.riskScoringRuleExecutions?.data;
  if (!riskScoringRuleExecutions) return 0;

  return riskScoringRuleExecutions.reduce((score, riskScoringRuleExecution) => {
    if (!riskScoringRuleExecution?.attributes?.skipped) {
      return score + riskScoringRuleExecution.attributes.score;
    } else {
      return score;
    }
  }, 0);
};

const textColorForRiskLevel = (level) => {
  switch (level) {
    case "low":
      return mainColors[0];
    case "medium":
      return mainColors[1];
    case "high":
      return mainColors[2];
    default:
      return "#000";
  }
};

const bgColorForRiskLevel = (level) => {
  switch (level) {
    case "low":
      return "#f0fdf4";
    case "medium":
      return "#fffbeb";
    case "high":
      return "#fef2f2";
    default:
      return "#000";
  }
};

watchEffect(() => {
  if (!riskScoring.value?.relationships?.riskScoringRuleExecutions?.data) {
    chartOption.value.series[0].data = [0, 0, 0];
    return;
  }
  chartOption.value.series[0].data = [
    countRiskLevelOccurences("low"),
    countRiskLevelOccurences("medium"),
    countRiskLevelOccurences("high"),
  ];
  chartOption.value.graphic[0].children[1].style.text = computeCurrentScore();
  chartOption.value.graphic[0].children[1].style.fill = textColorForRiskLevel(
    riskScoring.value?.attributes?.scoreLevel
  );
  chartOption.value.graphic[0].children[0].style.fill = bgColorForRiskLevel(
    riskScoring.value?.attributes?.scoreLevel
  );
});
</script>
